
import { Modal } from "bootstrap";
import { defineComponent, ref, inject, watchEffect } from "vue";
import { Input, Select } from "@/components/input-elements";
import { ConfigEditFormData } from "../interfaces";
import { Apollo, Notify } from "@/core/services";
import { GET_CONFIG } from "@/modules/common/configuration/graphql/Queries";
import { UPDATE_CONFIG } from "../graphql/Mutations";
import { useStore } from "vuex";
import { GET_COUNTRIES } from "@/modules/common/countries/graphql/Queries";
import { useI18n } from "vue-i18n";
import InnerLoader from "@/components/InnerLoader.vue";

export default defineComponent({
	name: "edit config",
	components: {
		Input,
		Select,
		InnerLoader,
	},

	setup() {
		const modal: any = ref();
		const loading = ref(false);
		const emitter: any = inject("emitter");
		const editConfigForm = ref<null | HTMLFormElement>(null);
		const sites = ref([]) as Record<any, any>;
		const store = useStore();
		const countries = ref([]) as Record<any, any>;
		const showCountryInfo = ref([]) as Record<any, any>;
		const disabled = ref(false);
		const site_id = ref(null);
		const getSites = store.getters.getAllSites.data.sites;
		const i18n = useI18n();

		const countriesData = Apollo.readQuery({
			query: GET_COUNTRIES,
		});
		// Given Site Form Interface
		const configData = ref<ConfigEditFormData>({
			name: "",
			country_id: null,
			site_id: null,
			is_default: false,
			id: null,
		});

		// Get All Sites
		watchEffect(() => {
			sites.value = [];

			getSites.forEach((element, index) => {
				sites.value.push({
					label: element.name,
					value: element.id,
				});
			});
		});

		// Get Countries
		const getCountries = () => {
			countries.value = [];

			const country = [] as Record<any, any>;
			countriesData.countries.forEach((element) => {
				country.push({
					label: element.name,
					value: element.id,
					locale: element.locale,
					currency: element.currency,
				});
			});

			countries.value = country;
		};

		const changeConfig = (id: number) => {
			showCountryInfo.value = [];
			const config = countries.value.find((element) => element.value == id);
			showCountryInfo.value.push({
				Currency: config.currency,
				Locale: config.locale,
			});
		};

		// Submit Hanlder Request
		const submitHandlerForm = async () => {
			editConfigForm.value?.validate(async (valid: boolean) => {
				if (valid) {
					const formData = {
						id: configData.value.id,
						name: configData.value.name,
						is_default: configData.value.is_default == false ? 0 : 1,
						country_id: configData.value.country_id,
						site_id: configData.value.site_id,
					};

					if (disabled.value == false) {
						loading.value = true;
						await Apollo.mutate({
							mutation: UPDATE_CONFIG,
							variables: { input: formData },
							update: (store, { data: { update_system_config } }) => {
								const pervious_record = store.readQuery({
									query: GET_CONFIG,
								}) as Record<any, any>;

								store.writeQuery({
									query: GET_CONFIG,
									data: {
										system_configuration: [...pervious_record.system_configuration],
									},
									variables: {
										id: update_system_config.id,
									},
								});
								modal.value.hide();
								loading.value = false;
								Notify.success(`${i18n.t("message.RECORD_UPDATED_SUCCESSFULLY")}`);
							},
						}).catch(() => {
							loading.value = false;
						});
					} else {
						Notify.error(i18n.t("message.SOMETHING_WENT_WRONG"));
					}
				}
			});
		};

		// Reset Form Data On Model Popup
		const resetForm = () => {
			getCountries();
			emitter.emit("clearInput");
			editConfigForm.value?.resetFields();
		};

		const changeSite = (id: number) => {
			const config = Apollo.readQuery({
				query: GET_CONFIG,
			});

			const siteCountryId = getSites.find((ele) => ele.id == id);

			configData.value.country_id = siteCountryId.country_id;
			changeConfig(siteCountryId.country_id);

			const exitsSite = config.system_configuration.find((ele) => ele.site.id == id);
			if (exitsSite != undefined) {
				if (configData.value.id) {
					if (site_id.value == id) {
						disabled.value = false;
						return;
					}
				}
				Notify.error("Selected Site Already Associate with other Configuration");
				disabled.value = true;
			} else {
				disabled.value = false;
			}
		};
		// Emitter To Open Model
		emitter.on("editSystemConfigAction", (config: Record<any, any>) => {
			resetForm();
			configData.value.id = config.id;
			configData.value.name = config.name;
			configData.value.country_id = config.country.id;
			configData.value.site_id = config.site.id;
			configData.value.is_default = config.is_default == 1 ? true : false;
			site_id.value = config.site.id;
			changeConfig(config.country.id);
			modal.value = new Modal(document.getElementById("modal_edit_config")) as HTMLElement;
			modal.value.show();
		});

		return {
			loading,
			configData,
			editConfigForm,
			sites,
			countries,
			disabled,
			showCountryInfo,
			changeSite,
			submitHandlerForm,
			changeConfig,
		};
	},
});
